import { useState, type ReactNode } from 'react';
import { Icon } from '../ui/Icon';
import { Select } from '../ui/Input';
import type { Cascada } from '../../hooks/useCascadaFiltros';
import estilos from './BarraFiltros.module.css';

/** Filtro puesto ahora mismo, para mostrarlo como chip retirable. */
export interface ChipFiltro {
  clave: string;
  etiqueta: string;
  quitar: () => void;
}

interface BarraFiltrosProps {
  /** Cascada Cliente → Área → Nodo. Omitirla oculta esos tres desplegables. */
  cascada?: Cascada;
  /** Filtros propios de la pantalla. */
  children?: ReactNode;
  acciones?: ReactNode;
  chips?: readonly ChipFiltro[];
}

/**
 * Barra de filtros de una pantalla.
 *
 * En pantalla ancha los filtros están siempre a la vista, uno al lado de otro.
 * En el móvil se recogen tras un botón: apilados ocupaban media pantalla y
 * empujaban los datos —que es a lo que se viene— por debajo del borde.
 *
 * Fuera del panel se quedan dos cosas, visibles siempre: las acciones —que no
 * siempre son filtros; en Alarmas es el indicador de que la pantalla está en
 * vivo y en Monitoreo el selector de vista— y los chips de filtros activos.
 *
 * Son la respuesta a «¿por qué no veo mis datos?», y esconderlos justo en la
 * pantalla donde no caben los filtros sería esconder la pista cuando más falta
 * hace. Por lo mismo el botón lleva la cuenta de cuántos hay puestos.
 */
export function BarraFiltros({ cascada, children, acciones, chips = [] }: BarraFiltrosProps) {
  const [abierto, setAbierto] = useState(false);

  return (
    <div className={estilos.barra}>
      {/* Sólo aparece en móvil; en escritorio los filtros están siempre puestos. */}
      <button
        type="button"
        className={estilos.botonFiltros}
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
      >
        <Icon nombre="filtrar" tamano={18} />
        Filtros
        {chips.length > 0 && <span className={estilos.contador}>{chips.length}</span>}
        <Icon nombre={abierto ? 'contraer' : 'expandir'} tamano={18} />
      </button>

      <div
        className={[estilos.panel, abierto ? estilos.panelAbierto : ''].filter(Boolean).join(' ')}
      >
        <div className={estilos.contenido}>
          {cascada && (
            <>
              {/* Con un solo cliente visible el desplegable no aporta nada. */}
              {cascada.clientes.length > 1 && (
                <div className={estilos.campo}>
                  <Select
                    etiqueta="Cliente"
                    placeholder="Todos los clientes"
                    value={cascada.seleccion.clienteId ?? ''}
                    onChange={(e) =>
                      cascada.elegirCliente(e.target.value ? Number(e.target.value) : null)
                    }
                    opciones={cascada.clientes.map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
                  />
                </div>
              )}

              <div className={estilos.campo}>
                <Select
                  etiqueta="Área"
                  placeholder={cascada.seleccion.clienteId ? 'Todas las áreas' : 'Elige un cliente'}
                  disabled={!cascada.seleccion.clienteId}
                  value={cascada.seleccion.areaId ?? ''}
                  onChange={(e) =>
                    cascada.elegirArea(e.target.value ? Number(e.target.value) : null)
                  }
                  opciones={cascada.areas.map((a) => ({ valor: a.id, etiqueta: a.nombre }))}
                />
              </div>

              <div className={estilos.campo}>
                <Select
                  etiqueta="Nodo"
                  placeholder={cascada.seleccion.areaId ? 'Todos los nodos' : 'Elige un área'}
                  disabled={!cascada.seleccion.areaId}
                  value={cascada.seleccion.nodoId ?? ''}
                  onChange={(e) =>
                    cascada.elegirNodo(e.target.value ? Number(e.target.value) : null)
                  }
                  opciones={cascada.nodos.map((n) => ({ valor: n.id, etiqueta: n.nombre }))}
                />
              </div>
            </>
          )}

          {children}
        </div>
      </div>

      {acciones && <div className={estilos.acciones}>{acciones}</div>}

      {chips.length > 0 && (
        <div className={estilos.chips}>
          <span className={estilos.etiquetaChips}>Filtros activos:</span>
          {chips.map((chip) => (
            <span key={chip.clave} className={estilos.chip}>
              {chip.etiqueta}
              <button
                type="button"
                className={estilos.quitarChip}
                onClick={chip.quitar}
                aria-label={`Quitar filtro ${chip.etiqueta}`}
              >
                <Icon nombre="cerrar" tamano={14} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
